import { useEffect, useId, useRef, useState } from 'react';
import { Table2, ChartColumn } from 'lucide-react';
import Button from './Button';
import Table from './Table';

// §2.6 (rev 2.3) the frame every chart primitive sits in: a heading that names the question, an optional note, and
// "Show as table" — the same numbers as a Table, for screen readers, print and anyone who wants the figures.
// Charts are SVG drawn at the frame's own pixel width (not a scaled viewBox), so text stays at its token size on a phone.

// The width of a box, kept current. Starts at `fallback` so the first paint already has a shape.
export function useBoxWidth(fallback = 560) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => { if (el.clientWidth) setWidth(el.clientWidth); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// Numbers: words are whole, minutes one decimal. Axis ticks shorten thousands (12K); figures never do.
export const formatAmount = (n, unit) => (n == null ? '' : unit === 'min'
  ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  : Math.round(Number(n)).toLocaleString('en-US'));
export const withUnit = (n, unit) => `${formatAmount(n, unit)} ${unit === 'min' ? 'min' : 'words'}`;
export const formatTick = (n, unit) => {
  if (unit === 'min') return Number.isInteger(n) ? String(n) : n.toFixed(1);
  return n >= 1000 ? `${(n / 1000).toFixed(n % 1000 ? 1 : 0).replace(/\.0$/, '')}K` : String(Math.round(n));
};
// A round top for an axis: 1, 2 or 5 times a power of ten, at or above the largest value.
export function niceMax(v) {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const m = [1, 2, 2.5, 5, 10].find(k => k * p >= v);
  return m * p;
}

export default function Chart({ title, note, table, className, children }) {
  const [asTable, setAsTable] = useState(false);
  const headId = useId();
  return (
    <figure className={`ui ui-chart${className ? ` ${className}` : ''}`} aria-labelledby={headId}>
      <figcaption className="ui-chart__head">
        <h3 className="ui-chart__title" id={headId}>{title}</h3>
        {note && <span className="ui-chart__note">{note}</span>}
        {table && (
          <Button variant="quiet" className="ui-chart__toggle ui-print-hide" icon={asTable ? ChartColumn : Table2} aria-pressed={asTable}
            onClick={() => setAsTable(v => !v)}>{asTable ? 'Show as chart' : 'Show as table'}</Button>
        )}
      </figcaption>
      {asTable && table
        ? <div className="ui-region"><Table caption={typeof title === 'string' ? title : undefined} columns={table.columns} rows={table.rows} rowKey={table.rowKey} /></div>
        : children}
    </figure>
  );
}

// The x labels a chart can afford: every point while they fit (about 44 px each), else evenly thinned, last one kept.
export function xLabelIndexes(n, plotWidth, each = 44) {
  const fit = Math.max(2, Math.floor(plotWidth / each));
  if (n <= fit) return new Set(Array.from({ length: n }, (_, i) => i));
  const step = Math.ceil(n / fit);
  const out = new Set();
  for (let i = 0; i < n; i += step) out.add(i);
  if (n - 1 - Math.max(...out) > step * 0.75) out.add(n - 1); else { out.delete(Math.max(...out)); out.add(n - 1); }
  return out;
}

// A recessive y axis: three hairlines (0, half, top) with their ticks on the left.
export function YAxis({ max, unit, x0, x1, y, show = true }) {
  return [0, max / 2, max].map(v => (
    <g key={v}>
      <line className="ui-chart__grid" x1={x0} x2={x1} y1={y(v)} y2={y(v)} />
      {show && <text className="ui-chart__axis" x={x0 - 8} y={y(v) + 4} textAnchor="end">{formatTick(v, unit)}</text>}
    </g>
  ));
}
